import { ChevronDown } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * THE FOLDABLE DOCUMENT HEADER (owner decision 2026-10-30: the header box
 * "should be dismissable (can be folded) because it takes too much space").
 *
 * One card for every editor that draws the big details grid: a toggle with a
 * chevron (aria-expanded / aria-controls) and, while folded, ONE summary line —
 * partner, date, currency, document code and status — so the reader still
 * knows which document they are typing into.
 *
 * The folded state is a property of the PERSON and the KIND of editor, not of
 * the document or the link, so it lives in localStorage keyed per editor type.
 * Storage may be unavailable (private window, blocked site data): every access
 * is wrapped, and the card simply opens unfolded.
 *
 * Folding HIDES the grid (the `hidden` attribute) rather than unmounting it,
 * so the fields keep their state and the save still reads every value.
 */

const STORAGE_PREFIX = 'palaccount.headerFolded.';

export function readHeaderFolded(storageKey: string): boolean {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`) === '1';
  } catch {
    return false;
  }
}

export function writeHeaderFolded(storageKey: string, folded: boolean): void {
  try {
    if (folded) localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, '1');
    else localStorage.removeItem(`${STORAGE_PREFIX}${storageKey}`);
  } catch {
    /* storage unavailable — the choice just does not stick */
  }
}

export interface HeaderSummary {
  partner?: string | null | undefined;
  date?: string | null | undefined;
  currency?: string | null | undefined;
  code?: string | null | undefined;
  status?: ReactNode;
}

export function CollapsibleHeaderCard({
  storageKey,
  summary,
  className,
  children,
}: {
  /** One per editor TYPE (e.g. 'salesInvoice', 'bill'). */
  storageKey: string;
  summary: HeaderSummary;
  /** The grid classes of the card's content (the editor's own layout). */
  className?: string | undefined;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const bodyId = useId();
  const [folded, setFolded] = useState(() => readHeaderFolded(storageKey));

  const toggle = () => {
    setFolded((current) => {
      const next = !current;
      writeHeaderFolded(storageKey, next);
      return next;
    });
  };

  const parts: ReactNode[] = [];
  if (summary.partner) parts.push(<span key="partner" className="font-medium text-foreground">{summary.partner}</span>);
  if (summary.date) parts.push(<Ltr key="date" value={summary.date} />);
  if (summary.currency) parts.push(<Ltr key="currency" value={summary.currency} className="font-mono" />);
  if (summary.code) parts.push(<Ltr key="code" value={summary.code} className="font-mono" />);

  return (
    <Card>
      <div className="flex items-center gap-3 px-6">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={!folded}
          aria-controls={bodyId}
          onClick={toggle}
          className="-ms-2"
        >
          <ChevronDown
            aria-hidden
            className={cn('size-4 transition-transform', folded ? '-rotate-90 rtl:rotate-90' : '')}
          />
          {folded ? t('docHeader.unfold') : t('docHeader.fold')}
        </Button>
        {folded ? (
          <p
            data-testid="header-summary"
            className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground"
          >
            {parts.map((part, index) => (
              <span key={index} className="inline-flex items-center gap-2">
                {index > 0 ? <span aria-hidden>·</span> : null}
                {part}
              </span>
            ))}
            {summary.status ? <span className="inline-flex items-center">{summary.status}</span> : null}
          </p>
        ) : null}
      </div>
      <CardContent id={bodyId} hidden={folded} className={cn(className, folded && 'hidden')}>
        {children}
      </CardContent>
    </Card>
  );
}
